/**
 * CompanionOnboardingContainer.tsx
 * Business logic container for the Companion onboarding flow.
 *
 * Responsibilities:
 * - Manage onboarding modal visibility
 * - Handle form submission with GraphQL mutation
 * - Trigger refetch of user data after completion
 * - Handle loading and error states
 *
 * Integration:
 * - Uses updateUserLearningPreferences mutation which saves preferences AND sets isOnboarded: true
 * - Integrates with useCompanionOnboardingForm hook for form state
 * - Phone verification happens in OnboardingStep6 component via verifyReminderPhone API
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CompanionOnboardingModal from "../../components/companion/onboarding/CompanionOnboardingModal";
import { useCompanionOnboardingForm } from "../../hooks/useCompanionOnboardingForm";
import {
  CheckInFrequency,
  GetNudgePreferencesDocument,
  StudyTime,
  useUpdateUserLearningPreferencesMutation,
} from "@axio/graphql/generated/graphql";
import { useAuth } from "../../hooks/useAuth";

interface CompanionOnboardingContainerProps {
  visible: boolean;
  onClose: () => void;
  isCompanionMode?: boolean;
}

function CompanionOnboardingContainer({ visible, onClose, isCompanionMode = true }: CompanionOnboardingContainerProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const { refetchMe } = useAuth();
  const [updateUserLearningPreferences] = useUpdateUserLearningPreferencesMutation();

  const toStudyTimePreference = (value?: string | null): StudyTime | undefined => {
    switch (value) {
      case "morning":
        return StudyTime.Morning;
      case "afternoon":
        return StudyTime.Afternoon;
      case "evening":
        return StudyTime.Evening;
      default:
        return undefined;
    }
  };

  const toCheckInFrequency = (value?: string | null): CheckInFrequency | undefined => {
    switch (value) {
      case "minimal":
        return CheckInFrequency.Minimal;
      case "normal":
        return CheckInFrequency.Normal;
      case "proactive":
        return CheckInFrequency.Proactive;
      default:
        return undefined;
    }
  };

  const goalOptions = [
    { key: "pass_finals", value: t("text.companion_onboarding_goal_pass_finals") },
    { key: "improve_gpa", value: t("text.companion_onboarding_goal_improve_gpa") },
    { key: "complete_prerequisites", value: t("text.companion_onboarding_goal_complete_prerequisites") },
    { key: "stay_on_track", value: t("text.companion_onboarding_goal_stay_on_track") },
  ];

  const {
    currentScreen,
    formData,
    canProceed,
    setValue,
    getValues,
    handleStart,
    handleNext,
    handleBack,
    handleSkip,
    goToSuccess,
    resetForm,
    setPhoneVerified,
    setSmsOptIn,
    smsOptIn,
  } = useCompanionOnboardingForm();

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setError(undefined);

      const values = getValues();

      const studyTimePreference = toStudyTimePreference(values.studyTimePreference);
      const checkInFrequency = toCheckInFrequency(values.checkInFrequency);

      const { data } = await updateUserLearningPreferences({
        variables: {
          input: {
            interests: values.interests ?? [],
            careerInterests: values.careerInterests ?? [],
            continuingEducation: values.continuingEducation ?? [],
            skillsToImprove: values.skillsToImprove ?? [],
            learningStyles: values.learningStyles ?? [],
            motivations: values.motivations ?? [],
            semesterGoal: values.semesterGoal ?? "",
            assignmentReminders: values.assignmentReminders ?? true,
            studyTimePreference,
            checkInFrequency,
            textReminders: values.textReminders ?? false,
            consentToSms: values.textReminders ? smsOptIn : false,
          },
        },
        refetchQueries: [{ query: GetNudgePreferencesDocument }],
      });

      if (data?.updateUserLearningPreferences) {
        // The backend mutation already sets isOnboarded: true
        // Show success screen - we'll refetch user data when they click "Start Chatting"
        goToSuccess();
      } else {
        setError(t("text.companion_onboarding_error_save"));
      }
    } catch (err) {
      console.error("Error submitting onboarding:", err);
      setError(t("text.companion_onboarding_error_save_retry"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    // Called when user clicks "Start Chatting" on success screen
    // The onboarding status (isOnboarded: true) was already set by updateUserLearningPreferences
    // Now refetch user data to update the UI, then close the modal
    await refetchMe();
    resetForm();
    onClose();
  };

  const handleModalSkip = () => {
    // When user clicks "Skip" on intro screen, dismiss modal without saving
    // Form is reset so if they open it again later, they start fresh
    resetForm();
    onClose();
  };

  return (
    <CompanionOnboardingModal
      visible={visible}
      currentScreen={currentScreen}
      formData={formData}
      setValue={setValue}
      onStart={handleStart}
      onNext={handleNext}
      onBack={handleBack}
      onSubmit={handleSubmit}
      onSkip={handleModalSkip}
      onComplete={handleComplete}
      canProceed={canProceed}
      isLoading={isLoading}
      error={error}
      goalOptions={goalOptions}
      setPhoneVerified={setPhoneVerified}
      setSmsOptIn={setSmsOptIn}
      isCompanionMode={isCompanionMode}
    />
  );
}

export default CompanionOnboardingContainer;
